import { useState, useEffect, SyntheticEvent } from 'react';
import cn from 'classnames';
import get from 'lodash/get';
/* Store Selectors */
import { useCartPlanSelector } from '@store/queries/cart/useCart.selector';
/* Constants */
import { UPSELL_PLAN_BILLING_CYCLES, MONTHLY_PLAN_ID } from 'src/constants/plan';
/* Components */
import { Radio } from 'ht-styleguide';
/* Tracking */
import { PlanSegment, SegmentTrackingAction, TRACKING_TYPES } from '@integrations/segment/upsell';
/* Styles */
import styles from './serviceinfo.module.scss';
/* Types */
import { TPlansInfo } from 'types/plans.types';
import { MergeSkuPrice } from 'types/sku.types';
import { formatPriceTypeHourly } from '@utils/currency';
import { BREAKDOWN_PRICE_TYPES } from '@constants/cart';
import { PartnerAPI } from '@integrations/partners/partners.types';

type ServiceInfoUpsellProps = {
  className: string;
  onUpsellToggle: BaseAnyFunction;
  onUpsellMore: BaseAnyFunction;
  serviceExtra: {
    rating: {
      rating: number;
      ratingCount: number;
    };
    prices: MergeSkuPrice;
    showUpsell: boolean;
    ctaText: string;
    upsellSelected: boolean;
    selectedPlanId: number;
    plansInfo: TPlansInfo;
    partner: PartnerAPI;
    priceType: keyof typeof BREAKDOWN_PRICE_TYPES;
    showFree: boolean;
  };
};

enum RadioOptionsEnum {
  PLAN = 'PLAN',
  NO_PLAN = 'NO_PLAN',
}

const PriceDiplay = ({ priceDisplay, showFree, priceHeaderStyles }: { priceHeaderStyles: string; priceDisplay: string; showFree: boolean }) => (
  <div>
    <p className={priceHeaderStyles}>Standard Price</p>
    <p className="p1 text-weight-medium">
      <span>{priceDisplay}</span>&nbsp;
      {!showFree && <span className="n700 text-weight-book">+ additional fees</span>}
    </p>
  </div>
);

const ServiceInfoUpsell = ({
  serviceExtra = {} as ServiceInfoUpsellProps['serviceExtra'], // This is sku.extra
  onUpsellToggle,
  onUpsellMore,
  className,
}: ServiceInfoUpsellProps) => {
  /* Data */
  const { showUpsell, plansInfo, prices, showFree } = serviceExtra || {};
  const { MONTHLY } = UPSELL_PLAN_BILLING_CYCLES;
  const monthlyPlanId = get(plansInfo, [MONTHLY, 'id'], MONTHLY_PLAN_ID);
  const { id: planIdInCart } = useCartPlanSelector();
  const isUpsellPlanInCart = planIdInCart === monthlyPlanId;
  const monthlyPlanAmount = get(plansInfo, [MONTHLY, 'amount']);

  /* Local State */
  const [radio, setRadio] = useState(isUpsellPlanInCart ? RadioOptionsEnum.PLAN : RadioOptionsEnum.NO_PLAN);
  const currentRadio = isUpsellPlanInCart ? RadioOptionsEnum.PLAN : radio;

  /* Styles */
  const standardContainerStyles = cn('padding-small marginBottom-small flex alignItemsCenter', styles.upsellContainer, {
    [styles.Radioselected]: currentRadio === RadioOptionsEnum.NO_PLAN,
  });
  const memberContainerStyles = cn(styles.upsellContainer, styles.memberContainer, {
    [styles.Radioselected]: currentRadio === RadioOptionsEnum.PLAN,
  });

  const priceHeaderStyles = cn('p2 text-weight-medium marginBottom-tiny');
  const priceDisplay = showFree ? 'Free' : formatPriceTypeHourly({ sku: serviceExtra, price: prices.price! });

  /* Methods */
  const onRadioChange = (selectedValue: RadioOptionsEnum) => (e: SyntheticEvent) => {
    if (e) e.preventDefault();
    setRadio(selectedValue);

    if (selectedValue === RadioOptionsEnum.PLAN) {
      SegmentTrackingAction.upsell(
        {
          plan: { ...get(plansInfo, [MONTHLY], {}) },
          partner: serviceExtra.partner,
        } as PlanSegment,
        TRACKING_TYPES.added,
      );
      onUpsellToggle(true, monthlyPlanId);
    }
    if (selectedValue === RadioOptionsEnum.NO_PLAN) {
      SegmentTrackingAction.upsell(
        {
          plan: { ...get(plansInfo, [MONTHLY], {}) },
          partner: serviceExtra.partner,
        } as PlanSegment,
        TRACKING_TYPES.removed,
      );
      onUpsellToggle(false, monthlyPlanId);
    }
  };

  const handleLearnMoreClick = (e: SyntheticEvent) => {
    if (e) e.stopPropagation();
    onUpsellMore();
  };

  /*
   Carted plan Id is the same as the monthly plan id.

   This is a sinister bug. It's too far down in the child.
   Radios are controlled by children, yet the setting of the cart is outside, in multiple areas.
   Inevitably, a mismatch happens because the radio is not being clicked, but the cart is being set.

   Probably best to lift the radio setting, also up to the parent.:q
*/
  useEffect(() => {
    if (showUpsell) {
      SegmentTrackingAction.upsell(
        {
          plan: { ...get(plansInfo, [MONTHLY], {}) },
          partner: serviceExtra.partner,
        } as PlanSegment,
        TRACKING_TYPES.viewed,
      );
    }
  }, [showUpsell, currentRadio, MONTHLY, plansInfo, serviceExtra.partner]);

  if (!showUpsell)
    return (
      <div className={standardContainerStyles}>
        <PriceDiplay priceDisplay={priceDisplay} showFree={showFree} priceHeaderStyles={priceHeaderStyles} />
      </div>
    );

  return (
    <form className={className}>
      <div className={standardContainerStyles} onClick={onRadioChange(RadioOptionsEnum.NO_PLAN)}>
        <Radio label="" value={RadioOptionsEnum.NO_PLAN} selected={currentRadio === RadioOptionsEnum.NO_PLAN} />
        <PriceDiplay priceDisplay={priceDisplay} showFree={showFree} priceHeaderStyles={priceHeaderStyles} />
      </div>

      <div className={cn('text-align-center', styles.upsellMemberLabel)}>{`SAVE ${prices.priceDifference || ''}`}</div>
      <div className={memberContainerStyles}>
        <div className="flex alignItemsCenter padding-small" onClick={onRadioChange(RadioOptionsEnum.PLAN)}>
          <Radio label="" value={RadioOptionsEnum.PLAN} selected={currentRadio === RadioOptionsEnum.PLAN} />
          <div>
            <p className={priceHeaderStyles}>Members Price</p>
            <p className="p1 text-weight-medium">
              <span className="n300 line-through">{prices.price}&nbsp;</span>
              <span className="blue700">{prices.memberPrice}&nbsp;</span>
              <span className="n700 text-weight-book">with ${monthlyPlanAmount}/mo annual membership.</span>
              &nbsp;
              <button type="button" className="plainButton underline blue700 text-link" onClick={handleLearnMoreClick}>
                Learn More
              </button>
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ServiceInfoUpsell;
